/**
 * Redstone block definitions. Like every other stateful block here (doors,
 * stairs, rails) the whole state lives in the block id, so save / load, the
 * wire protocol and the mesher need nothing extra: one id per
 * (kind, direction, power, variant, flags).
 *
 * blocks.ts calls `defineRedstoneBlocks` once while it builds the registry
 * (this file must not import values from blocks.ts: it runs in the middle of
 * that module's evaluation).
 *
 * Directions are face indices (blocks.ts FACE_DIRS): 0 +X, 1 -X, 2 +Y, 3 -Y,
 * 4 +Z, 5 -Z; `d ^ 1` is the opposite. What `dir` means per kind:
 *   torch / lever / button / tripwire hook   the neighbour it is attached to
 *   repeater / comparator                    the way the signal travels (output side)
 *   observer                                 the side it watches (output is the opposite)
 *   piston / piston head                     the way it extends
 */
import type { BlockDef, CollisionRect } from '../../blocks';

export const K = {
  NONE: 0, DUST: 1, TORCH: 2, LEVER: 3, BUTTON: 4, PLATE: 5, REPEATER: 6, COMPARATOR: 7, LAMP: 8,
  OBSERVER: 9, PISTON: 10, HEAD: 11, NOTE: 12, TARGET: 13, DAYLIGHT: 14, HOOK: 15, TRIPWIRE: 16,
} as const;

const MAX = 4096;
/** Component kind per block id (K.NONE for everything that is not a redstone component). */
export const RS_KIND = new Uint8Array(MAX);
export const RS_DIR = new Int8Array(MAX).fill(-1);
/** Signal the block holds / emits (0..15), or 0 / 1 for on-off parts. */
export const RS_POW = new Uint8Array(MAX);
/** Button 0 stone 1 oak; plate 0 stone 1 oak 2 light 3 heavy; repeater delay-1; comparator 0 compare 1 subtract;
 *  piston / head 1 sticky; daylight 1 inverted; note block pitch 0..24. */
export const RS_VAR = new Uint8Array(MAX);
/** Bit 0: repeater locked, piston extended, hook / tripwire attached. */
export const RS_FLAG = new Uint8Array(MAX);

const IDS = new Map<number, number>();
const key = (kind: number, dir: number, pow: number, variant: number, flag: number): number =>
  ((((kind * 8 + (dir + 1)) * 32 + pow) * 32 + variant) * 4 + flag);

/** The block id of a component state (-1 if there is none). */
export function rsId(kind: number, dir: number, pow: number, variant = 0, flag = 0): number {
  return IDS.get(key(kind, dir, pow, variant, flag)) ?? -1;
}
/** The same block with another power / flag / variant. */
export const withPow = (id: number, pow: number): number => rsId(RS_KIND[id], RS_DIR[id], pow, RS_VAR[id], RS_FLAG[id]);
export const withFlag = (id: number, flag: number): number => rsId(RS_KIND[id], RS_DIR[id], RS_POW[id], RS_VAR[id], flag);
export const withVar = (id: number, variant: number): number => rsId(RS_KIND[id], RS_DIR[id], RS_POW[id], variant, RS_FLAG[id]);

/** Inventory ids (the canonical state of each component); filled by defineRedstoneBlocks. */
export const RS = {
  DUST: -1, TORCH: -1, LEVER: -1, STONE_BUTTON: -1, OAK_BUTTON: -1, STONE_PLATE: -1, OAK_PLATE: -1, LIGHT_PLATE: -1, HEAVY_PLATE: -1,
  REPEATER: -1, COMPARATOR: -1, LAMP: -1, LAMP_ON: -1, OBSERVER: -1, PISTON: -1, STICKY_PISTON: -1, NOTE: -1, TARGET: -1,
  DAYLIGHT: -1, HOOK: -1, TRIPWIRE: -1,
};
/** Every id of each component, canonical first (mining / pick-block map them all to the canonical one). */
export const RS_GROUPS: number[][] = [];
/** Ids that hang on one neighbour: [id, face]. */
export const RS_ATTACH: Array<[number, number]> = [];
/** First and one-past-last redstone block id. */
export const RS_RANGE = { first: 0, end: 0 };

type Six = [number, number, number, number, number, number];
export interface DefApi {
  bid(): number;
  def(id: number, name: string, tiles: Six, opts?: Partial<BlockDef>): void;
  T: Record<string, number>;
  /** Register a wall torch id on a face / the wall ids of a standing torch (blocks.ts WALL_TORCHES, TORCH_WALLS). */
  wallTorch(id: number, face: number): void;
  torchWalls(base: number, byFace: number[]): void;
}

const CUTOUT = 1, MODEL = 3;
const all = (t: number): Six => [t, t, t, t, t, t];

/** A thin box against the face `dir`, `w` x `h` wide and `d` deep, in 1/16. */
export function attachedBox(dir: number, w: number, h: number, d: number): CollisionRect {
  const a = (16 - w) / 2, b = (16 - h) / 2;
  switch (dir) {
    case 0: return [16 - d, b, a, 16, 16 - b, 16 - a];
    case 1: return [0, b, a, d, 16 - b, 16 - a];
    case 2: return [a, 16 - d, b, 16 - a, 16, 16 - b];
    case 3: return [a, 0, b, 16 - a, d, 16 - b];
    case 4: return [a, b, 16 - d, 16 - a, 16 - b, 16];
    default: return [a, b, 0, 16 - a, 16 - b, d];
  }
}
/** The slab of a cell from `a` to `b` sixteenths along `dir`'s axis, measured from the side opposite `dir`. */
export function alongBox(dir: number, a: number, b: number, inset = 0): CollisionRect {
  const lo = [inset, inset, inset], hi = [16 - inset, 16 - inset, 16 - inset];
  const ax = dir >> 1;
  if ((dir & 1) === 0) { lo[ax] = a; hi[ax] = b; } else { lo[ax] = 16 - b; hi[ax] = 16 - a; }
  return [lo[0], lo[1], lo[2], hi[0], hi[1], hi[2]];
}

/** Target (outline) boxes of blocks without collision: [id, boxes]; blocks.ts merges them into TARGET_BOXES. */
export const RS_OUTLINES: Array<[number, readonly CollisionRect[]]> = [];

export function defineRedstoneBlocks(api: DefApi): void {
  const { T } = api;
  RS_RANGE.first = -1;
  let group: number[] = [];
  const reg = (name: string, tiles: Six, opts: Partial<BlockDef>, kind: number, dir: number, pow: number, variant = 0, flag = 0, outline?: readonly CollisionRect[]): number => {
    const id = api.bid();
    if (id >= MAX) throw new Error('redstone: block ids exhausted');
    if (RS_RANGE.first < 0) RS_RANGE.first = id;
    RS_RANGE.end = id + 1;
    api.def(id, name, tiles, { ...opts, placeable: group.length === 0 && opts.placeable !== false });
    RS_KIND[id] = kind; RS_DIR[id] = dir; RS_POW[id] = pow; RS_VAR[id] = variant; RS_FLAG[id] = flag;
    IDS.set(key(kind, dir, pow, variant, flag), id);
    group.push(id);
    if (outline) RS_OUTLINES.push([id, outline]);
    return id;
  };
  const start = (): void => { group = []; RS_GROUPS.push(group); };
  const thin = { solid: false, opaque: false, pass: CUTOUT, shape: MODEL, needsSupport: true, rigid: true } as const;

  // dust: power 0..15 (connections are worked out from the neighbours, power.ts dustSides)
  start();
  for (let p = 0; p < 16; p++) reg('Redstone Dust', all(T.RS_DUST_LINE), { ...thin, placeable: false }, K.DUST, -1, p, 0, 0, [[0, 0, 0, 16, 1, 16]]);
  RS.DUST = group[0];

  // redstone torch: standing lit is the inventory id; the wall ids follow the plain torch's scheme
  start();
  {
    const torch = (lit: number, face: number): number =>
      reg('Redstone Torch', all(lit ? T.RS_TORCH : T.RS_TORCH_OFF), { solid: false, opaque: false, pass: CUTOUT, shape: 1, needsSupport: true, torch: true, light: lit ? 7 : 0 }, K.TORCH, face, lit);
    RS.TORCH = torch(1, 3);
    const off = torch(0, 3);
    const litWalls: number[] = [], offWalls: number[] = [];
    for (const face of [0, 1, 4, 5]) {
      litWalls[face] = torch(1, face); api.wallTorch(litWalls[face], face);
      offWalls[face] = torch(0, face); api.wallTorch(offWalls[face], face);
    }
    api.torchWalls(RS.TORCH, litWalls);
    api.torchWalls(off, offWalls);
  }

  // lever: one id per attached face, off / on
  start();
  for (const dir of [3, 0, 1, 2, 4, 5]) for (let p = 0; p < 2; p++) {
    const id = reg('Lever', all(T.LEVER), thin, K.LEVER, dir, p, 0, 0, [attachedBox(dir, dir >> 1 === 1 ? 6 : 6, 8, 6)]);
    RS_ATTACH.push([id, dir]);
  }
  RS.LEVER = group[0];

  // buttons
  for (const [variant, name, tile] of [[0, 'Stone Button', T.STONE], [1, 'Oak Button', T.PLANKS]] as const) {
    start();
    for (const dir of [3, 0, 1, 2, 4, 5]) for (let p = 0; p < 2; p++) {
      const id = reg(name, all(tile), thin, K.BUTTON, dir, p, variant, 0, [attachedBox(dir, 6, 4, p ? 1 : 2)]);
      RS_ATTACH.push([id, dir]);
    }
    if (variant === 0) RS.STONE_BUTTON = group[0]; else RS.OAK_BUTTON = group[0];
  }

  // pressure plates: stone / oak on-off, weighted 0..15
  const plates: Array<[number, string, number, number]> = [
    [0, 'Stone Pressure Plate', T.STONE, 2], [1, 'Oak Pressure Plate', T.PLANKS, 2],
    [2, 'Light Weighted Pressure Plate', T.GOLD_BLOCK, 16], [3, 'Heavy Weighted Pressure Plate', T.IRON_BLOCK, 16],
  ];
  for (const [variant, name, tile, n] of plates) {
    start();
    for (let p = 0; p < n; p++) reg(name, all(tile), thin, K.PLATE, -1, p, variant, 0, [[1, 0, 1, 15, 1, 15]]);
    if (variant === 0) RS.STONE_PLATE = group[0]; else if (variant === 1) RS.OAK_PLATE = group[0];
    else if (variant === 2) RS.LIGHT_PLATE = group[0]; else RS.HEAVY_PLATE = group[0];
  }

  // repeater: facing x delay x powered x locked; comparator: facing x mode x output
  const slab = { ...thin, collision: [[0, 0, 0, 16, 2, 16]] as readonly CollisionRect[] };
  start();
  for (const dir of [5, 0, 4, 1]) for (let delay = 0; delay < 4; delay++) for (let p = 0; p < 2; p++) for (let lock = 0; lock < 2; lock++) {
    reg('Redstone Repeater', all(p ? T.REPEATER_ON : T.REPEATER), slab, K.REPEATER, dir, p, delay, lock);
  }
  RS.REPEATER = group[0];
  start();
  for (const dir of [5, 0, 4, 1]) for (let mode = 0; mode < 2; mode++) for (let p = 0; p < 16; p++) {
    reg('Redstone Comparator', all(p ? T.COMPARATOR_ON : T.COMPARATOR), slab, K.COMPARATOR, dir, p, mode);
  }
  RS.COMPARATOR = group[0];

  // lamp
  start();
  RS.LAMP = reg('Redstone Lamp', all(T.RS_LAMP), {}, K.LAMP, -1, 0);
  RS.LAMP_ON = reg('Redstone Lamp', all(T.RS_LAMP_ON), { light: 15 }, K.LAMP, -1, 1);

  // observer: `dir` is the watched side; the face shows there, the output dot on the back
  start();
  for (const dir of [5, 0, 1, 2, 3, 4]) for (let p = 0; p < 2; p++) {
    const tiles = all(T.OBSERVER_SIDE);
    if (dir >> 1 !== 1) { tiles[2] = T.OBSERVER_TOP; tiles[3] = T.OBSERVER_TOP; } else { tiles[4] = T.OBSERVER_TOP; tiles[5] = T.OBSERVER_TOP; }
    tiles[dir] = T.OBSERVER_FRONT;
    tiles[dir ^ 1] = p ? T.OBSERVER_BACK_ON : T.OBSERVER_BACK;
    reg('Observer', tiles, {}, K.OBSERVER, dir, p);
  }
  RS.OBSERVER = group[0];

  // pistons: base (retracted is a plain cube, extended a 12/16 body) and the head with its arm
  for (const sticky of [0, 1]) {
    start();
    const name = sticky ? 'Sticky Piston' : 'Piston';
    const face = sticky ? T.PISTON_TOP_STICKY : T.PISTON_TOP;
    for (const dir of [2, 0, 1, 3, 4, 5]) {
      const tiles = all(T.PISTON_SIDE);
      tiles[dir] = face; tiles[dir ^ 1] = T.PISTON_BOTTOM;
      reg(name, tiles, { opaque: false, shape: MODEL, pass: 0 }, K.PISTON, dir, 0, sticky, 0);
      const ext = all(T.PISTON_SIDE);
      ext[dir] = T.PISTON_INNER; ext[dir ^ 1] = T.PISTON_BOTTOM;
      reg(name, ext, { opaque: false, shape: MODEL, pass: 0, collision: [alongBox(dir, 0, 12)] }, K.PISTON, dir, 1, sticky, 1);
    }
    if (sticky) RS.STICKY_PISTON = group[0]; else RS.PISTON = group[0];
    for (const dir of [2, 0, 1, 3, 4, 5]) {
      const tiles = all(T.PISTON_SIDE);
      tiles[dir] = face; tiles[dir ^ 1] = T.PISTON_TOP;
      reg('Piston Head', tiles, { opaque: false, shape: MODEL, pass: 0, placeable: false, collision: [alongBox(dir, 12, 16), alongBox(dir, -4, 12, 6)] }, K.HEAD, dir, 0, sticky);
    }
  }

  // note block: pitch 0..24, and whether it is currently powered (so it only plays on the rising edge)
  start();
  for (let p = 0; p < 2; p++) for (let pitch = 0; pitch < 25; pitch++) reg('Note Block', all(T.NOTE_BLOCK), {}, K.NOTE, -1, p, pitch);
  RS.NOTE = group[0];

  // target: output 0..15
  start();
  for (let p = 0; p < 16; p++) reg('Target', [T.TARGET_SIDE, T.TARGET_SIDE, T.TARGET_TOP, T.TARGET_TOP, T.TARGET_SIDE, T.TARGET_SIDE], {}, K.TARGET, -1, p);
  RS.TARGET = group[0];

  // daylight detector: normal / inverted x output
  start();
  for (let inv = 0; inv < 2; inv++) for (let p = 0; p < 16; p++) {
    const top = inv ? T.DAYLIGHT_INV_TOP : T.DAYLIGHT_TOP;
    reg('Daylight Detector', [T.DAYLIGHT_SIDE, T.DAYLIGHT_SIDE, top, T.DAYLIGHT_SIDE, T.DAYLIGHT_SIDE, T.DAYLIGHT_SIDE],
      { opaque: false, shape: MODEL, pass: 0, collision: [[0, 0, 0, 16, 6, 16]] }, K.DAYLIGHT, -1, p, inv);
  }
  RS.DAYLIGHT = group[0];

  // tripwire hook (attached to a wall) and tripwire (placed with string)
  start();
  for (const dir of [5, 0, 1, 4]) for (let att = 0; att < 2; att++) for (let p = 0; p < 2; p++) {
    const id = reg('Tripwire Hook', all(T.TRIPWIRE_HOOK), thin, K.HOOK, dir, p, 0, att, [attachedBox(dir, 6, 10, 6)]);
    RS_ATTACH.push([id, dir]);
  }
  RS.HOOK = group[0];
  start();
  for (let att = 0; att < 2; att++) for (let p = 0; p < 2; p++) {
    reg('Tripwire', all(T.TRIPWIRE), { ...thin, needsSupport: false, placeable: false }, K.TRIPWIRE, -1, p, 0, att, [[0, 1, 0, 16, 2.5, 16]]);
  }
  RS.TRIPWIRE = group[0];
}

/** The inventory id a redstone block mines / picks as (itself for everything else). */
export function rsCanonical(id: number): number {
  if (RS_KIND[id] === K.NONE) return id;
  if (RS_KIND[id] === K.HEAD) return 0;
  for (const g of RS_GROUPS) if (g.indexOf(id) >= 0) return g[0];
  return id;
}
